import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, Activity, Info } from 'lucide-react';
import { useEPSESites } from '@/hooks/useEPSESites';
import { EPSESiteCard } from '@/components/epse/EPSESiteCard';
import { EPSESiteModal } from '@/components/epse/EPSESiteModal';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';

export default function CRMEPSEOverview() {
  const { sites, isLoading, createSite, deleteSite } = useEPSESites();
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  
  const filteredSites = sites?.filter(site => 
    site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    site.organization?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    site.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Activity className="h-6 w-6" />
              Energy Profile & Simulation Engine
            </h1>
            <p className="text-muted-foreground">
              Analyseer energieprofielen en simuleer BESS scenario's
            </p>
          </div>
          
          {/* Info HoverCard */}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                <Info className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors" />
              </Button>
            </HoverCardTrigger>
            <HoverCardContent className="w-[400px] p-0" align="start">
              <div className="p-4 space-y-4">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  📊 Hoe werkt EPSE?
                </h4>
                
                <div className="space-y-2">
                  <p className="font-medium text-sm text-primary">Kwartierprofielen opslaan:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Upload een Excel/CSV met echte kwartierdata (kW of kWh)</li>
                    <li>Het systeem detecteert automatisch de juiste kolommen</li>
                    <li>Kies handmatig kW of kWh indien de header niet duidelijk is</li>
                    <li>Profielen worden genormaliseerd en per seizoen/dagtype opgeslagen</li>
                  </ol>
                </div>
                
                <div className="space-y-2">
                  <p className="font-medium text-sm text-primary">Synthetische profielen verbeteren:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Ga naar een site met opgeslagen kwartierdata</li>
                    <li>Het systeem leert patronen uit uw echte metingen</li>
                    <li>Bij "Synthetisch profiel" worden deze patronen toegepast</li>
                    <li>De vorm (pieken, daluurtje, weekend) is gebaseerd op uw data</li>
                  </ol>
                </div>
                
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="text-sm">
                    <span className="font-medium">💡 Tip:</span>{' '}
                    Hoe meer sites u analyseert met echte kwartierdata, hoe nauwkeuriger 
                    de synthetische profielen worden voor vergelijkbare locaties en sectoren.
                  </p>
                </div>

                <div className="space-y-2 pt-2 border-t">
                  <p className="font-medium text-sm text-primary">Optionele modules:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    <li><strong>Zonnepanelen (PV)</strong> - Voeg PV productie toe aan de simulatie</li>
                    <li><strong>Batterij (BESS)</strong> - Simuleer peak shaving met een batterij</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2">
                    Beide modules kunnen onafhankelijk aan/uit gezet worden in de site configuratie.
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>
        
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nieuwe Site
        </Button>
      </div>
      
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Zoeken op naam, organisatie of tag..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : filteredSites && filteredSites.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSites.map(site => (
            <EPSESiteCard 
              key={site.id} 
              site={site} 
              onDelete={(id) => setDeleteId(id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-muted/30 rounded-lg">
          <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium">Geen sites gevonden</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'Probeer een andere zoekterm' : 'Maak je eerste site aan om te beginnen'}
          </p>
          {!searchQuery && (
            <Button onClick={() => setModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nieuwe Site
            </Button>
          )}
        </div>
      )}
      
      <EPSESiteModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSave={(data) => createSite.mutate(data)}
      />
      
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Site verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie verwijdert de site en alle bijbehorende data permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) {
                  deleteSite.mutate(deleteId);
                  setDeleteId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
